import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Bell,
  BookOpen,
  CheckCircle2,
  ClipboardCopy,
  Lock,
  LogOut,
  Megaphone,
  PlayCircle,
  Sparkles,
  Users,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import BeforeYouJoinFAQ from '../public/BeforeYouJoinFAQ';
import ActivationBriefingCard from './ActivationBriefingCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  bg: '#0c1117',
  bgAlt: '#121821',
  card: '#171f2b',
  border: '#1f2937',
  text: '#f8fafc',
  textMuted: '#94a3b8',
  accent: '#eab308',
  accentSoft: 'rgba(234,179,8,0.12)',
  success: '#22c55e',
  warn: '#f59e0b',
};

const LockedToolCard = ({ name, blurb }) => (
  <div
    className="rounded-xl p-5 border relative overflow-hidden"
    style={{ backgroundColor: COLORS.bgAlt, borderColor: COLORS.border }}
  >
    <div className="flex items-center gap-2 mb-2">
      <Lock className="h-4 w-4" style={{ color: COLORS.textMuted }} />
      <span className="text-xs uppercase tracking-wider" style={{ color: COLORS.textMuted }}>
        Locked preview
      </span>
    </div>
    <h4 className="font-semibold mb-1" style={{ color: COLORS.text }}>
      {name}
    </h4>
    <p className="text-sm" style={{ color: COLORS.textMuted }}>
      {blurb}
    </p>
  </div>
);

const queueLabel = (s) => {
  switch (s) {
    case 'personal':
      return { label: 'Personal queue', color: COLORS.accent };
    case 'network':
      return { label: 'Network queue', color: COLORS.warn };
    case 'global':
      return { label: 'Global queue', color: COLORS.textMuted };
    case 'claimed':
      return { label: 'Claimed', color: COLORS.success };
    case 'activated':
      return { label: 'Activated', color: COLORS.success };
    default:
      return { label: s, color: COLORS.textMuted };
  }
};

const WaitlistMemberDashboard = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [briefingStatus, setBriefingStatus] = useState(null);
  const isReferralUnlocked = briefingStatus?.acknowledged && !briefingStatus?.requires_reack;

  const fetchBriefingStatus = async (tok) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/member/waitlist/briefing/status`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      const json = await res.json();
      if (json.ok) setBriefingStatus(json);
    } catch (e) {/* silent */}
  };

  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    mobile_phone: '',
    wallet_trc20: '',
    needs_wallet_help: false,
  });

  useEffect(() => {
    const t = localStorage.getItem('bbc_waitlist_token');
    if (!t) {
      navigate('/waitlist');
      return;
    }
    setToken(t);
    fetchDashboard(t);
    fetchBriefingStatus(t);
  }, []);

  const fetchDashboard = async (tok) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/waitlist/dashboard`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.status === 401) {
        localStorage.removeItem('bbc_waitlist_token');
        navigate('/waitlist');
        return;
      }
      const json = await res.json();
      if (json.ok) {
        setData(json);
        setProfileForm({
          first_name: json.profile.first_name || '',
          last_name: json.profile.last_name || '',
          mobile_phone: json.profile.mobile_phone || '',
          wallet_trc20: json.profile.wallet_trc20 || '',
          needs_wallet_help: !!json.profile.needs_wallet_help,
        });
      }
    } catch (e) {
      console.error('dashboard fetch failed', e);
      toast.error('Could not load your dashboard.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!token) return;
    setSavingProfile(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/waitlist/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...profileForm,
          wallet_trc20: profileForm.wallet_trc20.trim() || null,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        toast.error(json.detail || 'Failed to save profile');
        return;
      }
      toast.success('Profile updated');
      fetchDashboard(token);
    } catch (err) {
      toast.error('Network error');
    } finally {
      setSavingProfile(false);
    }
  };

  const copyLink = (link) => {
    if (!isReferralUnlocked) {
      toast.error('Complete the Activation Briefing first.');
      return;
    }
    try {
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied');
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = link;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
        toast.success('Referral link copied');
      } catch {
        toast.error('Copy failed — long-press the link to copy manually.');
      }
      document.body.removeChild(el);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('bbc_waitlist_token');
    localStorage.removeItem('bbc_waitlist_signup');
    navigate('/waitlist');
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: COLORS.bg }}
      >
        <div
          className="animate-spin rounded-full h-10 w-10 border-b-2"
          style={{ borderColor: COLORS.accent }}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
      >
        <Card className="border-0 max-w-md w-full" style={{ backgroundColor: COLORS.card }}>
          <CardContent className="p-8 text-center">
            <p className="mb-4">We couldn't load your back-office.</p>
            <Button onClick={() => navigate('/waitlist')} style={{ backgroundColor: COLORS.accent, color: COLORS.bg }}>
              Back to waitlist
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const queueBadge = queueLabel(data.profile.queue_state);

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.bg, color: COLORS.text }}>
      {/* Top bar */}
      <div
        className="border-b"
        style={{ borderColor: COLORS.border, backgroundColor: COLORS.bgAlt }}
      >
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center font-bold"
              style={{ backgroundColor: COLORS.accent, color: COLORS.bg }}
            >
              BBC
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: COLORS.text }}>
                Waiting List Back-Office
              </p>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>
                Limited access · pre-launch
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            style={{ borderColor: COLORS.border, color: COLORS.text }}
            data-testid="wl-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Hello + queue state */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold" style={{ color: COLORS.text }}>
            Hey {data.profile.first_name}.
          </h1>
          <p className="mt-2 text-base md:text-lg" style={{ color: COLORS.textMuted }}>
            Your position is recorded. Full builder tools open when admin activates launch.
          </p>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <Badge
              className="px-3 py-1"
              style={{
                backgroundColor: 'transparent',
                color: queueBadge.color,
                border: `1px solid ${queueBadge.color}55`,
              }}
            >
              {queueBadge.label}
            </Badge>
            {data.full_access_enabled ? (
              <Badge
                className="px-3 py-1"
                style={{
                  backgroundColor: 'rgba(34,197,94,0.12)',
                  color: COLORS.success,
                  border: `1px solid ${COLORS.success}40`,
                }}
              >
                <Sparkles className="h-3 w-3 mr-1.5 inline" />
                Launch is live — upgrade available
              </Badge>
            ) : (
              <Badge
                className="px-3 py-1"
                style={{
                  backgroundColor: COLORS.accentSoft,
                  color: COLORS.accent,
                  border: `1px solid ${COLORS.accent}40`,
                }}
              >
                <Lock className="h-3 w-3 mr-1.5 inline" />
                Full access opens when admin activates launch
              </Badge>
            )}
          </div>
        </div>

        {/* Activation briefing (only when full access enabled) */}
        {data.full_access_enabled && (
          <Card className="border-0" style={{ backgroundColor: COLORS.card }} data-testid="activation-briefing">
            <CardContent className="p-6 md:p-8">
              <Badge
                className="mb-3"
                style={{
                  backgroundColor: 'rgba(34,197,94,0.12)',
                  color: COLORS.success,
                  border: `1px solid ${COLORS.success}40`,
                }}
              >
                Builder Activation Briefing
              </Badge>
              <h2 className="text-2xl font-bold mb-3" style={{ color: COLORS.text }}>
                {data.activation_briefing.headline}
              </h2>
              <p className="mb-4" style={{ color: COLORS.textMuted }}>
                {data.activation_briefing.body}
              </p>
              {data.activation_briefing.video_url ? (
                <div
                  className="aspect-video rounded-xl overflow-hidden"
                  style={{ backgroundColor: '#000' }}
                >
                  {/youtube|youtu\.be|vimeo/i.test(data.activation_briefing.video_url) ? (
                    <iframe
                      src={data.activation_briefing.video_url
                        .replace('watch?v=', 'embed/')
                        .replace('youtu.be/', 'www.youtube.com/embed/')}
                      title="Activation briefing"
                      className="w-full h-full"
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={data.activation_briefing.video_url}
                      controls
                      playsInline
                      className="w-full h-full"
                    />
                  )}
                </div>
              ) : null}
              <Link to="/register" className="inline-block mt-4">
                <Button style={{ backgroundColor: COLORS.accent, color: COLORS.bg }}>
                  Upgrade to full member
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Activation Briefing — must be completed before referral link unlocks */}
        {token && (
          <ActivationBriefingCard
            waitlistToken={token}
            onAcknowledged={() => fetchBriefingStatus(token)}
          />
        )}

        {/* Referral link */}
        <Card
          className="border-0 relative"
          style={{ backgroundColor: COLORS.card }}
          data-testid="referral-link-card"
        >
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5" style={{ color: COLORS.accent }} />
              <h2 className="text-xl font-bold" style={{ color: COLORS.text }}>
                Your waiting list referral link
              </h2>
              {!isReferralUnlocked && (
                <span
                  className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border"
                  style={{ color: COLORS.warn, borderColor: COLORS.warn, backgroundColor: 'rgba(234,179,8,0.08)' }}
                  data-testid="referral-locked-badge"
                >
                  <Lock className="h-3 w-3 mr-1" /> Locked until briefing
                </span>
              )}
            </div>
            <p className="text-sm mb-4" style={{ color: COLORS.textMuted }}>
              {isReferralUnlocked
                ? 'Anyone who signs up through this link joins your Personal Pay-It-Forward list first.'
                : 'Complete the Activation Briefing above to unlock your personal referral link.'}
            </p>
            <div
              className="rounded-xl p-4 border flex items-center justify-between gap-3 flex-wrap relative overflow-hidden"
              style={{ backgroundColor: COLORS.bgAlt, borderColor: COLORS.border }}
              aria-disabled={!isReferralUnlocked}
            >
              <code
                className={`text-sm break-all ${isReferralUnlocked ? '' : 'select-none pointer-events-none'}`}
                style={{
                  color: COLORS.accent,
                  filter: isReferralUnlocked ? 'none' : 'blur(5px)',
                  transition: 'filter 0.3s',
                }}
                data-testid="dashboard-referral-link"
              >
                {data.referral_link}
              </code>
              <Button
                size="sm"
                disabled={!isReferralUnlocked}
                onClick={() => copyLink(data.referral_link)}
                style={{
                  backgroundColor: isReferralUnlocked ? COLORS.accent : COLORS.border,
                  color: COLORS.bg,
                  cursor: isReferralUnlocked ? 'pointer' : 'not-allowed',
                  opacity: isReferralUnlocked ? 1 : 0.6,
                }}
                data-testid="dashboard-copy-btn"
              >
                <ClipboardCopy className="h-4 w-4 mr-2" />
                {isReferralUnlocked ? 'Copy' : 'Locked'}
              </Button>
            </div>
            <p className="text-xs mt-3" style={{ color: COLORS.textMuted }}>
              Code: <span style={{ color: COLORS.text }}>{data.profile.my_referral_code}</span>
            </p>
          </CardContent>
        </Card>

        {/* Two-column: Profile + Wallet notice */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-0" style={{ backgroundColor: COLORS.card }}>
            <CardContent className="p-6 md:p-8">
              <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.text }}>
                Basic profile
              </h3>
              <form onSubmit={handleSaveProfile} className="space-y-4" data-testid="wl-profile-form">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    placeholder="First name"
                    className="border-0"
                    style={{ backgroundColor: COLORS.bgAlt, color: COLORS.text }}
                    data-testid="wl-profile-first-name"
                  />
                  <Input
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                    placeholder="Last name"
                    className="border-0"
                    style={{ backgroundColor: COLORS.bgAlt, color: COLORS.text }}
                    data-testid="wl-profile-last-name"
                  />
                </div>
                <Input
                  value={profileForm.mobile_phone}
                  onChange={(e) => setProfileForm({ ...profileForm, mobile_phone: e.target.value })}
                  placeholder="Mobile phone"
                  className="border-0"
                  style={{ backgroundColor: COLORS.bgAlt, color: COLORS.text }}
                  data-testid="wl-profile-mobile"
                />
                <Input
                  value={profileForm.wallet_trc20}
                  onChange={(e) => setProfileForm({ ...profileForm, wallet_trc20: e.target.value })}
                  placeholder="TRON TRC-20 wallet (optional)"
                  className="border-0"
                  style={{ backgroundColor: COLORS.bgAlt, color: COLORS.text }}
                  data-testid="wl-profile-wallet"
                />
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="profile-wallet-help"
                    checked={profileForm.needs_wallet_help}
                    onCheckedChange={(checked) =>
                      setProfileForm({ ...profileForm, needs_wallet_help: checked === true })
                    }
                    data-testid="wl-profile-wallet-help"
                  />
                  <label htmlFor="profile-wallet-help" className="text-sm cursor-pointer" style={{ color: COLORS.text }}>
                    I need help creating a wallet
                  </label>
                </div>
                <Button
                  type="submit"
                  disabled={savingProfile}
                  style={{ backgroundColor: COLORS.accent, color: COLORS.bg }}
                  data-testid="wl-profile-save"
                >
                  {savingProfile ? 'Saving…' : 'Save profile'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-0" style={{ backgroundColor: COLORS.card }}>
            <CardContent className="p-6 md:p-8">
              <div className="flex items-start gap-3 mb-3">
                <Wallet className="h-6 w-6 mt-0.5" style={{ color: COLORS.accent }} />
                <div>
                  <h3 className="text-lg font-bold" style={{ color: COLORS.text }}>
                    Why we ask for a wallet
                  </h3>
                  <p className="text-sm mt-1" style={{ color: COLORS.textMuted }}>
                    BBC Quick List uses direct, member-to-member crypto payments. Without a wallet
                    on file you can hold a waiting list position, but you won't be able to
                    receive Pay-It-Forward credits or matrix payouts at launch.
                  </p>
                </div>
              </div>
              <ul className="space-y-2 text-sm" style={{ color: COLORS.text }}>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5" style={{ color: COLORS.success }} />
                  Wallet is optional during signup
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5" style={{ color: COLORS.success }} />
                  You can add it any time before launch
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5" style={{ color: COLORS.success }} />
                  Tick "I need help" and we'll guide you through it
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Direct referrals */}
        <Card className="border-0" style={{ backgroundColor: COLORS.card }}>
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold" style={{ color: COLORS.text }}>
                  My direct waiting list referrals
                </h3>
                <p className="text-sm" style={{ color: COLORS.textMuted }}>
                  Everyone who signed up using your link.
                </p>
              </div>
              <Badge
                style={{
                  backgroundColor: COLORS.accentSoft,
                  color: COLORS.accent,
                  border: `1px solid ${COLORS.accent}40`,
                }}
              >
                {data.direct_referrals.length} total
              </Badge>
            </div>
            {data.direct_referrals.length === 0 ? (
              <p className="text-sm" style={{ color: COLORS.textMuted }}>
                No direct referrals yet. Share your link above to start building your list.
              </p>
            ) : (
              <div className="space-y-2">
                {data.direct_referrals.map((r) => {
                  const q = queueLabel(r.queue_state);
                  return (
                    <div
                      key={r.id}
                      className="rounded-lg p-3 border flex items-center justify-between gap-3"
                      style={{ backgroundColor: COLORS.bgAlt, borderColor: COLORS.border }}
                    >
                      <div>
                        <p className="font-medium text-sm" style={{ color: COLORS.text }}>
                          {r.first_name} {r.last_name}
                        </p>
                        <p className="text-xs" style={{ color: COLORS.textMuted }}>
                          {r.email}
                        </p>
                      </div>
                      <Badge
                        style={{
                          backgroundColor: 'transparent',
                          color: q.color,
                          border: `1px solid ${q.color}55`,
                        }}
                      >
                        {q.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending PIF */}
        <Card className="border-0" style={{ backgroundColor: COLORS.card }}>
          <CardContent className="p-6 md:p-8">
            <h3 className="text-lg font-bold mb-1" style={{ color: COLORS.text }}>
              Pending Pay-It-Forward
            </h3>
            <p className="text-sm mb-4" style={{ color: COLORS.textMuted }}>
              Prospects sitting in your Personal queue. If you don't pay them forward before the timer expires,
              they release to your Network queue, then to Global.
            </p>
            {data.pending_pay_it_forward.length === 0 ? (
              <p className="text-sm" style={{ color: COLORS.textMuted }}>
                Your Personal Pay-It-Forward queue is empty right now.
              </p>
            ) : (
              <div className="space-y-2">
                {data.pending_pay_it_forward.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-lg p-3 border"
                    style={{ backgroundColor: COLORS.bgAlt, borderColor: COLORS.border }}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm" style={{ color: COLORS.text }}>
                        {p.first_name} {p.last_name}
                      </p>
                      <Badge
                        style={{
                          backgroundColor: 'transparent',
                          color: COLORS.accent,
                          border: `1px solid ${COLORS.accent}55`,
                        }}
                      >
                        Releases at {p.personal_window_expires_at
                          ? new Date(p.personal_window_expires_at).toLocaleString()
                          : '—'}
                      </Badge>
                    </div>
                    <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
                      {p.email}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Announcements + Training */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-0" style={{ backgroundColor: COLORS.card }}>
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4">
                <Megaphone className="h-5 w-5" style={{ color: COLORS.accent }} />
                <h3 className="text-lg font-bold" style={{ color: COLORS.text }}>
                  Admin announcements
                </h3>
              </div>
              {data.announcements.length === 0 ? (
                <p className="text-sm" style={{ color: COLORS.textMuted }}>
                  No announcements yet.
                </p>
              ) : (
                <ul className="space-y-3">
                  {data.announcements.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-lg p-3 border"
                      style={{ backgroundColor: COLORS.bgAlt, borderColor: COLORS.border }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {a.pinned && <Bell className="h-3 w-3" style={{ color: COLORS.accent }} />}
                        <p className="font-medium text-sm" style={{ color: COLORS.text }}>
                          {a.title}
                        </p>
                      </div>
                      {a.body && (
                        <p className="text-sm whitespace-pre-line" style={{ color: COLORS.textMuted }}>
                          {a.body}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-0" style={{ backgroundColor: COLORS.card }}>
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="h-5 w-5" style={{ color: COLORS.accent }} />
                <h3 className="text-lg font-bold" style={{ color: COLORS.text }}>
                  Training & updates
                </h3>
              </div>
              {data.training.length === 0 ? (
                <p className="text-sm" style={{ color: COLORS.textMuted }}>
                  Training items will appear here.
                </p>
              ) : (
                <ul className="space-y-3">
                  {data.training.map((t) => (
                    <li
                      key={t.id}
                      className="rounded-lg p-3 border"
                      style={{ backgroundColor: COLORS.bgAlt, borderColor: COLORS.border }}
                    >
                      <p className="font-medium text-sm mb-1" style={{ color: COLORS.text }}>
                        {t.title}
                      </p>
                      {t.body && (
                        <p className="text-sm mb-2 whitespace-pre-line" style={{ color: COLORS.textMuted }}>
                          {t.body}
                        </p>
                      )}
                      {t.video_url && (
                        <a
                          href={t.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs"
                          style={{ color: COLORS.accent }}
                        >
                          <PlayCircle className="h-3 w-3" />
                          Watch
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Locked tool previews */}
        <Card className="border-0" style={{ backgroundColor: COLORS.card }}>
          <CardContent className="p-6 md:p-8">
            <h3 className="text-lg font-bold mb-1" style={{ color: COLORS.text }}>
              Builder tools — locked preview
            </h3>
            <p className="text-sm mb-4" style={{ color: COLORS.textMuted }}>
              A taste of what unlocks when admin flips launch live.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <LockedToolCard
                name="12×3 Matrix"
                blurb="Your personal matrix infrastructure that auto-repurchases as you grow."
              />
              <LockedToolCard
                name="Lead Capture Builder"
                blurb="Drag-and-drop templates for capture pages, link-in-bio, and link rotators."
              />
              <LockedToolCard
                name="Network Feed"
                blurb="Private mailbox, announcements, and direct member-to-member messaging."
              />
            </div>
          </CardContent>
        </Card>

        {/* Before You Join — compliance-friendly FAQ inside back-office */}
        <BeforeYouJoinFAQ variant="dark" />
      </div>
    </div>
  );
};

export default WaitlistMemberDashboard;
